import { Injectable, inject } from '@angular/core';
import { isObject } from 'lodash';
import { sourceIdColors } from '../../../cross-team-shared/constants';
import {
  convertHexToRGB,
  denormalizeRGB,
} from '../../../cross-team-shared/misc-utils/color-utils';
import {
  isPointCloudOutput,
  isVideoOutput,
} from '../../../cross-team-shared/misc-utils/output-utils';
import {
  RegionOfInterestState,
  defaultRegionOfInterestDistance,
} from '../../../cross-team-shared/state/region-of-interest-state';
import { ObjectWith, RGBA } from '../../../cross-team-shared/types/misc';
import { PointCloudOutput } from '../../../team-apis/otterdam/outputs/point-cloud-output';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';

type PartialPath = string | [string, PartialPath[]];

/*
 example of sub property path:
 const persistedSettings: PartialPath[] = [
  'layerSettings2d',
  ['layerSettings3d', ['groundingConfiguration', 'highlightedSensor']],
];
 */
const persistedSettings: PartialPath[] = [
  'layerSettings2d',
  'layerSettings3d',
  'persist',
  'useEvenSplit',
  'regionOfInterestSettings',
  'viewBoxHighlightEnabled',
];

@Injectable()
export class SettingsLoadingService {
  private taskWorkspaceStore = inject(TaskWorkspaceStore);

  load() {
    this.taskWorkspaceStore.batch(() => {
      this.loadPointAppearanceBySensor();
      this.loadRegionOfInterestSettingsDefaults();
      this.loadFromLocalStorage();
    });
  }

  private loadFromLocalStorage() {
    const { project } = this.taskWorkspaceStore.state();
    const asString = localStorage.getItem(settingsStorageKey(project.id));

    if (!asString) {
      return;
    }

    const parsed = JSON.parse(asString);
    this.taskWorkspaceStore('settings').setUsing((settings) => {
      persistedSettings.forEach((key) => this.assign(key, settings, parsed));
      return settings;
    });
  }

  private loadPointAppearanceBySensor() {
    const { project } = this.taskWorkspaceStore.state();
    const pointCloudOutput = project.outputs.find(isPointCloudOutput);

    if (!pointCloudOutput) {
      return;
    }

    this.taskWorkspaceStore('settings')('layerSettings3d')(
      'pointAppearanceBySensor',
    ).set(buildPointAppearanceBySensor(pointCloudOutput));
  }

  private assign(path: PartialPath, target: any, source: any) {
    const [key, childPaths] = formatPath(path);
    const targetValue = target[key];
    const sourceValue = source[key];

    if (typeof targetValue !== typeof sourceValue) {
      return;
    }

    if (isObject(targetValue) && !Array.isArray(targetValue)) {
      (childPaths ?? Object.keys(targetValue)).forEach((childKey) =>
        this.assign(childKey, targetValue, sourceValue),
      );
    } else {
      target[key] = sourceValue;
    }
  }

  private loadRegionOfInterestSettingsDefaults() {
    const { project } = this.taskWorkspaceStore.state();
    const videoOutputNames = project.outputs
      .filter(
        (output) =>
          isVideoOutput(output) && !output.meta.is_orthographic_projection,
      )
      .map((output) => output.name);

    const distancePerCamera: RegionOfInterestState['distancePerCamera'] = {};

    videoOutputNames.forEach((outputName) => {
      // this might be a project setting in the future
      distancePerCamera[outputName] = defaultRegionOfInterestDistance;
    });

    this.taskWorkspaceStore('settings')('regionOfInterestSettings')(
      'distancePerCamera',
    ).set(distancePerCamera);
  }
}

export function settingsStorageKey(projectId: number) {
  return `settings:${projectId}`;
}

function formatPath(path: PartialPath): [string, PartialPath[] | undefined] {
  return isObject(path) ? path : [path, undefined];
}

export function buildPointAppearanceBySensor(output: PointCloudOutput) {
  const sensors = output.meta.multi_point_cloud?.sensors;
  const pointAppearanceBySensor: ObjectWith<RGBA> = {};
  if (sensors) {
    sensors.forEach((sensor, i) => {
      const color = sourceIdColors[i % sourceIdColors.length];
      const [r, g, b] = convertHexToRGB(color).map((index) =>
        denormalizeRGB(index),
      );

      pointAppearanceBySensor[sensor.source_id] = [r, g, b, 1];
    });
  }

  return pointAppearanceBySensor;
}
